import Modal from 'components/Layout/Modal';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Dropdown from 'components/form/Dropdown';
import TextInput from 'components/form/TextInput';
import {
  convertString,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import { userRole } from 'utils/constants';
import MultipleSelect from 'components/form/MultipleSelect';
import { useEffect, useState } from 'react';
import { fetchCreateUserDetails, fetchState, showSuccess } from 'store/slices';
import CheckBox from 'components/form/CheckBox';
import Button from 'components/form/Button';
import Loader from 'components/Layout/Loader';
import { isEqual } from 'lodash';
import Label from 'components/form/Label';
import { getDataFromLocalStorage, numberOnlyFromInput } from 'utils/helpers';
import { handleUserCreation } from 'store/slices';
import { useParams } from 'react-router-dom';

const ResourceFormModal = ({
  onHide,
  onSubmit,
  requestId,
  getUserCreationRequestsData,
  btnLoading,
  setBtnLoading,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const { userType } = params;
  const [stateLoading, setStateLoading] = useState(false);
  const [stateList, setStateList] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const loginUserRole = getDataFromLocalStorage('user_type');
  const { countryList } = useSelector((state) => ({
    countryList: state.global.countryList,
  }));

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required.'),
    last_name: Yup.string(),
    email_id: Yup.string()
      .email('Email must be a valid email')
      .required('Email is required.'),
    phone_number: Yup.string()
      .required('Phone number is required.')
      .min(10, 'Phone number must be 10 digit.')
      .max(10, 'Phone number must be 10 digit.'),
    state: Yup.string().when('user_type', {
      is: (val) => val === '6.1' || val === '6.2',
      then: (schema) => schema.required('State/Province is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),

    country: Yup.string().when('user_type', {
      is: (val) => val === '6.1' || val === '6.2',
      then: (schema) => schema.required('Country is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const initialValues = {
    first_name: userDetails?.first_name || '',
    last_name: userDetails?.last_name || '',
    email_id: userDetails?.email_id || '',
    phone_number: userDetails?.phone_number || '',
    country_code: userDetails?.country_code || 'IN',
    state: userDetails?.state || '',
    country: userDetails?.country || '',
    user_type: userDetails?.user_type || userType || '',
  };

  const getCreateUserDetails = async () => {
    const response = await dispatch(
      fetchCreateUserDetails({ request_id: requestId })
    );
    if (response.status === 200) {
      setUserDetails(response?.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (requestId) {
      getCreateUserDetails();
    } else {
      setIsLoading(false);
    }
  }, [requestId]);

  const handleEditConfirm = async (payload) => {
    let finalData = objectToFormData(payload);
    const response = await dispatch(handleUserCreation(finalData));
    if (response?.status === 200) {
      dispatch(showSuccess('User Request Updated!'));
      onHide();
      getUserCreationRequestsData();
    }
  };

  const handelSave = (values) => {
    let cState = values?.state?.split(',');
    let newStateList = [];
    cState.forEach((sElem) => {
      let find = null;
      Object.entries(stateList).forEach((elem) => {
        if (!find) {
          find = elem[1].find((o) => `${o.id}` === sElem);
        }
      });
      if (find) {
        newStateList.push(sElem);
      }
    });
    let newStateString = newStateList.toString();
    let newValues = { ...values, state: newStateString };
    if (requestId) {
      const payload = {
        ...values,
        state: newStateString,
        is_edit: true,
        request_id: requestId,
      };
      handleEditConfirm(payload);
    } else {
      setBtnLoading(true);
      onSubmit(newValues);
    }
  };

  const getStateFromCountry = async (countryIds) => {
    setStateLoading(true);
    const response = await dispatch(
      fetchState({ country_id: countryIds, is_resource: '1' })
    );
    setStateList(response?.data || {});
    setStateLoading(false);
  };

  useEffect(() => {
    if (userDetails?.country) {
      getStateFromCountry(userDetails?.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  const filteredOptions = userRole?.filter((item) => item.id !== loginUserRole);

  return (
    <Modal
      size="lg"
      onHide={onHide}
      titleBesideClose={requestId ? 'Edit User' : 'Add New User'}
      titleBesideCloseClassName={'cps-15'}
      isTitleBorder
      isTitleVerticalCenter
    >
      <div className="cps-20 cpe-20 cpt-20 cpb-20">
        {isLoading ? (
          <Loader size="sm" />
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handelSave}
            validationSchema={validationSchema}
          >
            {(props) => {
              const {
                values,
                errors,
                handleChange,
                handleSubmit,
                setFieldValue,
                setFieldError,
              } = props;

              let viewCountryList =
                countryList?.filter((o) =>
                  values?.country?.split(',').includes(o.id)
                ) || [];

              return (
                <form>
                  <div className="row">
                    <div className="col-md-6 cmb-22">
                      <TextInput
                        label="First Name"
                        placeholder="First Name"
                        id="first_name"
                        onChange={(e) => {
                          setFieldValue(
                            'first_name',
                            titleCaseString(e.target.value)
                          );
                        }}
                        value={values.first_name}
                        error={errors.first_name}
                      />
                    </div>
                    <div className="col-md-6 cmb-22">
                      <TextInput
                        label="Last Name"
                        placeholder="Last Name"
                        id="last_name"
                        onChange={(e) => {
                          setFieldValue(
                            'last_name',
                            titleCaseString(e.target.value)
                          );
                        }}
                        value={values.last_name}
                        error={errors.last_name}
                      />
                    </div>
                    <div className="col-md-6 cmb-22">
                      <TextInput
                        label="Email Id"
                        placeholder="Email Id"
                        id="email_id"
                        onChange={(e) => {
                          handleChange(convertString(1, e));
                        }}
                        value={values.email_id}
                        error={errors.email_id}
                      />
                    </div>
                    <div className="col-md-6 cmb-22">
                      <TextInput
                        isPhoneNumber
                        label="Phone Number"
                        id="phone_number"
                        type="number"
                        placeholder="Enter Phone Number"
                        value={values?.phone_number}
                        error={errors?.phone_number}
                        phoneNumberData={{
                          id: 'country_code',
                          value: values.country_code,
                        }}
                        onChange={(e) => {
                          if (e.target.id === 'phone_number') {
                            handleChange(numberOnlyFromInput(e));
                          } else {
                            handleChange(e);
                            handleChange({
                              target: { id: 'phone_number', value: '' },
                            });
                            // setFieldValue('mobile_no_length', e.target.length);
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 cmb-22">
                      <Dropdown
                        label="User Type"
                        placeholder="Select Role"
                        options={filteredOptions}
                        id="user_type"
                        onChange={handleChange}
                        value={values.user_type}
                        error={errors.user_type}
                        optionKey="id"
                        optionValue="title"
                        disabled={userType}
                      />
                    </div>
                    <div className="col-md-6 cmb-22">
                      <MultipleSelect
                        label="Country"
                        placeholder="Select Allocated Country"
                        id="country"
                        value={values.country}
                        error={errors.country}
                        onChange={(e) => {
                          if (e?.target?.value) {
                            getStateFromCountry(e?.target?.value);
                          }
                          handleChange(e);
                        }}
                        optionValue="country"
                        options={countryList}
                      />
                    </div>
                    <div className="col-md-12 cmb-22">
                      <Label label="State" />
                      {viewCountryList?.length > 0 && !stateLoading ? (
                        <div className="border ps-3 pt-2 pe-3">
                          {viewCountryList?.map((cElem, cIndex) => {
                            let dStateList = stateList[cElem?.id] || [];
                            let tState = values?.state
                              ? values?.state?.split(',')
                              : [];
                            // console.log("STATE", tState);
                            let currentCountryState = tState?.filter((o) =>
                              dStateList.find((x) => x.id === +o)
                            );
                            return (
                              <div key={cIndex} className="mb-2">
                                <div className="d-flex gap-2 align-items-center">
                                  <span>
                                    <CheckBox
                                      type="ACTIVE"
                                      onClick={() => {
                                        if (
                                          currentCountryState.length ===
                                          dStateList.length
                                        ) {
                                          let newS = tState;
                                          dStateList.forEach((x) => {
                                            newS = newS.filter(
                                              (o) => o !== `${x?.id}`
                                            );
                                          });

                                          setFieldValue(
                                            'state',
                                            newS?.toString()
                                          );
                                        } else {
                                          let newS = tState;
                                          dStateList.forEach((x) => {
                                            newS = newS.filter(
                                              (o) => o !== `${x?.id}`
                                            );
                                          });
                                          let allState = [
                                            ...newS,
                                            ...dStateList.map((o) => `${o.id}`),
                                          ];

                                          setFieldValue(
                                            'state',
                                            allState?.toString()
                                          );
                                        }
                                      }}
                                      isChecked={
                                        currentCountryState.length ===
                                        dStateList.length
                                      }
                                    />
                                  </span>
                                  <span className="text-15-400">
                                    {cElem.country}
                                  </span>
                                </div>
                                <div className="cms-20 mt-1 d-flex gap-3 flex-wrap">
                                  {dStateList?.map((sElem, sIndex) => {
                                    const { id: sID, name } = sElem;
                                    let isCheck = false;
                                    if (values.state) {
                                      isCheck = values?.state
                                        ?.split(',')
                                        ?.includes(`${sID}`);
                                    }
                                    return (
                                      <div
                                        className="d-flex gap-2 align-items-center"
                                        key={sIndex}
                                      >
                                        <span>
                                          <CheckBox
                                            type="ACTIVE"
                                            isChecked={isCheck}
                                            onClick={() => {
                                              let tempArray = [];
                                              if (values.state) {
                                                tempArray =
                                                  values.state?.split(',');

                                                if (
                                                  tempArray?.includes(`${sID}`)
                                                ) {
                                                  tempArray = tempArray.filter(
                                                    (o) => o !== `${sID}`
                                                  );
                                                } else {
                                                  tempArray.push(`${sID}`);
                                                }
                                                let stateStr =
                                                  tempArray.length > 0
                                                    ? tempArray.toString()
                                                    : '';
                                                setFieldValue(
                                                  'state',
                                                  stateStr
                                                );
                                              } else {
                                                tempArray = [`${sElem.id}`];
                                                setFieldValue(
                                                  'state',
                                                  tempArray.toString()
                                                );
                                              }
                                            }}
                                          />
                                        </span>
                                        <span className="text-15-400">
                                          {name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div
                          className={`fake-input color-dark-silver bg-american-silver ${
                            stateLoading
                              ? 'justify-content-center bg-white'
                              : ''
                          }`}
                        >
                          {stateLoading ? (
                            <Loader size="sm" />
                          ) : (
                            'Select State/Province'
                          )}
                        </div>
                      )}
                      {errors.state && (
                        <span className="text-13-400 pt-1">
                          <span style={{ color: 'red' }}>{errors.state}</span>
                        </span>
                      )}
                    </div>

                    <div className="d-flex gap-4 cmt-20">
                      <Button
                        text={
                          loginUserRole === '1'
                            ? 'Submit'
                            : requestId
                            ? 'Update Request'
                            : 'Send Request'
                        }
                        btnStyle="primary-dark"
                        className="cps-30 cpe-30 h-35"
                        btnLoading={btnLoading}
                        disabled={isEqual(values, initialValues) || btnLoading}
                        onClick={(e) => {
                          let isStateMissing = false;
                          if (values.state) {
                            let selectedStateID = values.state?.split(',');
                            Object.entries(stateList).forEach((elem) => {
                              if (!isStateMissing) {
                                let ids = elem[1]?.map((o) => {
                                  return `${o.id}`;
                                });
                                var foundIds = selectedStateID.filter(
                                  (x) => ids.indexOf(x) !== -1
                                );
                                isStateMissing = foundIds.length === 0;
                              }
                            });
                          }
                          if (isStateMissing) {
                            setFieldError(
                              'state',
                              'Atleast one state should be select for country'
                            );
                          } else {
                            handleSubmit(e);
                          }
                        }}
                      />
                      <Button
                        text="Cancel"
                        btnStyle="light-outline"
                        className="cps-20 cpe-20 h-35"
                        onClick={onHide}
                      />
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default ResourceFormModal;
